import type { ReactNode, Ref } from 'react';
import { Box, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import ChevronRightRounded from '@mui/icons-material/ChevronRightRounded';

export interface RequestTypeCardProps {
  /** The request-type title (sentence case, translated). */
  title: string;
  /** One-line description of the type. */
  description: string;
  /** Leading glyph (an MUI icon element). */
  icon: ReactNode;
  onSelect: () => void;
  ref?: Ref<HTMLButtonElement>;
}

/**
 * Selectable launcher card (the Polaris kit `ReqTypeCard`) — an icon tile, the
 * type title + description, and a trailing chevron. Hover raises an accent
 * border + subtle shadow. Used by the "New request" launcher; each card routes
 * to its form.
 */
export function RequestTypeCard({ title, description, icon, onSelect, ref }: RequestTypeCardProps) {
  const theme = useTheme();
  const accent = theme.palette.secondary.main;

  return (
    <Box
      component="button"
      type="button"
      ref={ref}
      onClick={onSelect}
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.75,
        textAlign: 'left',
        width: '100%',
        p: '18px 18px 18px 16px',
        cursor: 'pointer',
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        transition: 'border-color 120ms, box-shadow 120ms',
        '&:hover, &:focus-visible': {
          borderColor: accent,
          boxShadow: `0 0 0 1px ${accent}, 0 4px 12px rgba(4,29,51,0.08)`,
          outline: 'none',
        },
        '&:hover .rtc-chevron, &:focus-visible .rtc-chevron': { color: accent },
      }}
    >
      <Box
        aria-hidden
        sx={{
          display: 'grid',
          placeItems: 'center',
          width: 40,
          height: 40,
          flexShrink: 0,
          borderRadius: 2,
          bgcolor: alpha(accent, 0.08),
          color: accent,
        }}
      >
        {icon}
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 0, flex: 1 }}>
        <Typography
          sx={{ fontSize: 14.5, fontWeight: 600, color: 'text.primary', lineHeight: 1.3 }}
        >
          {title}
        </Typography>
        <Typography sx={{ fontSize: 13, color: 'text.secondary', lineHeight: 1.45 }}>
          {description}
        </Typography>
      </Box>
      <ChevronRightRounded
        className="rtc-chevron"
        sx={{
          fontSize: 20,
          color: 'text.secondary',
          flexShrink: 0,
          alignSelf: 'center',
          transition: 'color 120ms',
        }}
      />
    </Box>
  );
}
